/*
 * CEO Challenge — shared UI primitives (theme + LemonBot burst + confetti).
 * Kept intentionally small so pages can import what they need without
 * pulling a global stylesheet.
 */
import { useEffect, useState } from "react";

/** Deep-navy quiz theme — reused across every CEO Challenge screen. */
export const THEME = {
  bg: "bg-[#0B1E3F]",
  bgSoft: "bg-[#132A54]",
  accentYellow: "#FFD500",
  accentPurple: "#7C3AED",
  accentBlue: "#38BDF8",
  accentGreen: "#22C55E",
  accentRed: "#F87171",
};

/** Small stylized LemonBot bubble that pulses on mount. */
export function LemonBot({ size = 96, message = null, mood = "happy", "data-testid": testId }) {
  const eyebrow = mood === "sad" ? "😟" : mood === "cheer" ? "🤩" : "😄";
  return (
    <div className="flex items-start gap-3" data-testid={testId || "lemonbot"}>
      <div
        className="relative shrink-0 rounded-full bg-[#FFD500] ring-4 ring-[#FFF5A6] shadow-[0_10px_30px_rgba(255,213,0,0.35)]"
        style={{ width: size, height: size }}
      >
        <div
          className="absolute inset-0 flex items-center justify-center text-3xl select-none"
          style={{ fontSize: size / 2.2 }}
          aria-hidden="true"
        >
          {eyebrow}
        </div>
      </div>
      {message && (
        <div className="relative bg-white text-[#0B1E3F] rounded-2xl px-4 py-3 shadow-lg max-w-xs">
          <div
            aria-hidden="true"
            className="absolute -left-2 top-4 w-3 h-3 bg-white rotate-45"
          />
          <p className="text-sm font-semibold leading-snug">{message}</p>
        </div>
      )}
    </div>
  );
}

/** Lightweight confetti burst — plain divs, no external lib. */
export function Confetti({ active, count = 60 }) {
  const [pieces, setPieces] = useState([]);
  useEffect(() => {
    if (!active) return;
    const colors = ["#FFD500", "#7C3AED", "#38BDF8", "#22C55E", "#F472B6"];
    const arr = Array.from({ length: count }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.3,
      rotate: Math.random() * 360,
      color: colors[i % colors.length],
      dur: 1.2 + Math.random() * 0.8,
    }));
    setPieces(arr);
    const t = setTimeout(() => setPieces([]), 2000);
    return () => clearTimeout(t);
  }, [active, count]);

  if (!pieces.length) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" data-testid="ceo-confetti">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="absolute top-0 block w-2 h-3 rounded-sm"
          style={{
            left: `${p.left}%`,
            background: p.color,
            transform: `rotate(${p.rotate}deg)`,
            animation: `ceoConfettiFall ${p.dur}s ease-in ${p.delay}s forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes ceoConfettiFall {
          0%   { transform: translate3d(0,-10vh,0) rotate(0deg); opacity: 1; }
          100% { transform: translate3d(0,110vh,0) rotate(720deg); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}

/** Category chip pill. */
export function Chip({ children, tone = "yellow" }) {
  const toneMap = {
    yellow: "bg-[#FFD500]/15 text-[#FFD500] ring-[#FFD500]/40",
    purple: "bg-[#7C3AED]/20 text-[#C4B5FD] ring-[#7C3AED]/40",
    blue: "bg-[#38BDF8]/15 text-[#7DD3FC] ring-[#38BDF8]/40",
    green: "bg-[#22C55E]/15 text-[#86EFAC] ring-[#22C55E]/40",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-widest ring-1 ${toneMap[tone] || toneMap.yellow}`}
    >
      {children}
    </span>
  );
}
